import { createConsumer } from '@rails/actioncable';
import voipCallsAPI from 'dashboard/api/voipCalls';
import CallRecordsAPI from 'dashboard/api/callRecords';
import { useCallsStore } from 'dashboard/stores/calls';
import { useCallCopilotStore } from 'dashboard/stores/callCopilot';
import {
  useStackZapMediaSession,
  cleanupStackZapMediaSession,
  prewarmStackZapAudioContext,
} from 'dashboard/composables/useStackZapMediaSession';
import { VOICE_CALL_PROVIDERS } from 'dashboard/helper/inbox';
import { VOICE_CALL_DIRECTION } from 'dashboard/components-next/message/constants';
import { emitter } from 'shared/helpers/mitt';

// Authenticated VoipCallChannel listener + StackZap control/media orchestration.
// Never receives/stores provider secrets, base URLs, or WS credentials.

const TERMINAL = new Set(['ended', 'missed', 'rejected', 'failed']);
/** Total attempts for inbound accept/reject when bind is still pending (1 + retries). */
const BINDING_PENDING_MAX_ATTEMPTS = 3;
/** Short deterministic backoff between binding-pending retries (ms). */
const BINDING_PENDING_DELAYS_MS = [100, 200];

const mediaSession = useStackZapMediaSession();
const stopStackZapMedia = () => {
  mediaSession.stop();
  cleanupStackZapMediaSession();
};
const startStackZapMedia = args => mediaSession.start(args);
const armStackZapRecording = () => {
  if (typeof mediaSession.startRecording === 'function') {
    return mediaSession.startRecording();
  }
  return false;
};
const setStackZapMediaMuted = muted => mediaSession.setMuted(muted);

/**
 * Call-scoped media release for cancel/dismiss. Prefer releaseForCall so a
 * stacked/foreign active session is never torn down by another call's cancel.
 * Falls back to global stop only when the scoped API is unavailable.
 * @param {number|string|null|undefined} callRecordId
 * @returns {boolean}
 */
function releaseStackZapMediaForCall(callRecordId) {
  if (callRecordId == null || callRecordId === '') return false;
  try {
    if (typeof mediaSession.releaseForCall === 'function') {
      return Boolean(mediaSession.releaseForCall(callRecordId));
    }
  } catch {
    /* ignore scoped failure; fall through */
  }
  // Legacy single-session path (tests/mocks without releaseForCall).
  stopStackZapMedia();
  return true;
}

/**
 * Create-fail / create-cancel cleanup for unowned gesture prewarm only.
 * Never fullStops an active foreign session (call A while attempt B fails).
 * @returns {boolean}
 */
function disposeUnownedStackZapPrewarm() {
  try {
    if (typeof mediaSession.disposeUnownedPrewarm === 'function') {
      return Boolean(mediaSession.disposeUnownedPrewarm());
    }
  } catch {
    /* ignore */
  }
  return false;
}

/**
 * Fail-closed: media must be live and owned by this call before join succeeds.
 * Prevents silent session_stale start from marking the call active without mic.
 * @param {number|string} callRecordId
 */
function assertMediaActiveForCall(callRecordId) {
  if (typeof mediaSession.isActiveForCall === 'function') {
    if (!mediaSession.isActiveForCall(callRecordId)) {
      const err = new Error('media_aborted');
      err.name = 'MediaSessionError';
      err.code = 'media_aborted';
      throw err;
    }
    return;
  }
  // Legacy mock without ownership probe: require isActive when available.
  if (typeof mediaSession.isActive === 'function' && !mediaSession.isActive()) {
    const err = new Error('media_aborted');
    err.name = 'MediaSessionError';
    err.code = 'media_aborted';
    throw err;
  }
}

/**
 * Gesture-first AudioContext unlock. Must run synchronously on the click path
 * before any accept/create/getUserMedia await spends user activation.
 * Idempotent: reuses prewarmed or live session context.
 * Optional callRecordId tags ownership for cancel cleanup during create→join.
 * @param {number|string|null|undefined} [callRecordId]
 */
function prewarmStackZapAudio(callRecordId) {
  try {
    if (typeof mediaSession.prewarmAudioContext === 'function') {
      return mediaSession.prewarmAudioContext(callRecordId);
    }
    return prewarmStackZapAudioContext(callRecordId);
  } catch {
    return null;
  }
}

function sleep(ms) {
  return new Promise(resolve => {
    setTimeout(resolve, ms);
  });
}

function isCallBindingPendingError(err) {
  return err?.response?.data?.error_code === 'call_binding_pending';
}

/**
 * Retry only call_binding_pending for inbound accept/reject.
 * Never retries timeout, 5xx, control_in_progress, state_sync_pending, or other errors.
 */
export async function withCallBindingPendingRetry(apiCall) {
  let lastError;
  for (let attempt = 1; attempt <= BINDING_PENDING_MAX_ATTEMPTS; attempt += 1) {
    try {
      // eslint-disable-next-line no-await-in-loop -- sequential limited retries
      return await apiCall();
    } catch (err) {
      lastError = err;
      const canRetry =
        isCallBindingPendingError(err) &&
        attempt < BINDING_PENDING_MAX_ATTEMPTS;
      if (!canRetry) throw err;
      const delayMs =
        BINDING_PENDING_DELAYS_MS[attempt - 1] ??
        BINDING_PENDING_DELAYS_MS[BINDING_PENDING_DELAYS_MS.length - 1];
      // eslint-disable-next-line no-await-in-loop -- sequential limited retries
      await sleep(delayMs);
    }
  }
  throw lastError;
}

function extensionForBlob(blob) {
  const type = (blob?.type || '').toLowerCase();
  if (type.includes('ogg')) return 'ogg';
  if (type.includes('mp4') || type.includes('m4a')) return 'm4a';
  if (type.includes('wav')) return 'wav';
  if (type.includes('mpeg') || type.includes('mp3')) return 'mp3';
  return 'webm';
}

/**
 * Finalize MediaRecorder once, upload non-empty blob, always clean media/mic.
 * Empty / unavailable recording → no upload (safe).
 * Uses bounded binding-pending retries so a race with webhook binding does not
 * silently drop the final blob before the last allowed lookup.
 */
async function finalizeAndUploadRecording(callRecordIdHint) {
  let result = {
    blob: null,
    recordingUnavailableReason: null,
    callRecordId: null,
  };
  try {
    if (typeof mediaSession.stopAndGetRecording === 'function') {
      result = await mediaSession.stopAndGetRecording();
    } else {
      stopStackZapMedia();
    }
  } catch {
    stopStackZapMedia();
  }

  const callRecordId = callRecordIdHint || result.callRecordId;
  const blob = result.blob;
  if (!blob || !blob.size || !callRecordId) {
    return {
      uploaded: false,
      reason: result.recordingUnavailableReason || 'empty',
    };
  }

  const filename = `call-recording-${callRecordId}.${extensionForBlob(blob)}`;
  try {
    await withCallBindingPendingRetry(() =>
      CallRecordsAPI.uploadRecording(callRecordId, blob, filename)
    );
    return { uploaded: true };
  } catch {
    // Hangup / terminal UI must not break on upload failure; media already cleaned.
    return { uploaded: false, reason: 'upload_failed' };
  }
}

/** @type {null | {
 *  accountId: number|string,
 *  userId: number|string,
 *  pubsubToken: string,
 *  consumer: any,
 *  subscription: any,
 *  store: any,
 *  dispositionDone: Set<string>,
 * }} */
let listener = null;

/** Module-level disposition bookkeeping (survives listener teardown for tests). */
const dispositionDone = new Set();
const dispositionInFlight = new Set();

/**
 * Call-scoped join/create cancellation latch.
 * handleCancelled marks ids so in-flight accept/create/start cannot resume
 * into orphan media or hang up another agent's accepted call.
 * Bounded Map (insertion order) — oldest entries drop when over capacity.
 */
const CANCELLED_JOIN_MAX = 64;
/** @type {Map<string, true>} */
const cancelledJoinIds = new Map();

const JOIN_CANCELLED = 'join_cancelled';

function markJoinCancelled(callRecordId) {
  if (callRecordId == null || callRecordId === '') return;
  const key = String(callRecordId);
  // Refresh insertion order so repeated cancels stay fresh in the bound window.
  cancelledJoinIds.delete(key);
  cancelledJoinIds.set(key, true);
  while (cancelledJoinIds.size > CANCELLED_JOIN_MAX) {
    const oldest = cancelledJoinIds.keys().next().value;
    cancelledJoinIds.delete(oldest);
  }
}

function isJoinCancelled(callRecordId) {
  if (callRecordId == null || callRecordId === '') return false;
  return cancelledJoinIds.has(String(callRecordId));
}

function clearJoinCancelled(callRecordId) {
  if (callRecordId == null || callRecordId === '') return;
  cancelledJoinIds.delete(String(callRecordId));
}

function isJoinCancelledError(err) {
  return (
    err?.code === JOIN_CANCELLED ||
    err?.message === JOIN_CANCELLED ||
    err?.name === 'JoinCancelledError'
  );
}

function joinCancelledError() {
  const err = new Error(JOIN_CANCELLED);
  err.name = 'JoinCancelledError';
  err.code = JOIN_CANCELLED;
  return err;
}

/** Throw when this call id was cancelled while join/create was in flight. */
function assertJoinNotCancelled(callRecordId) {
  if (isJoinCancelled(callRecordId)) {
    throw joinCancelledError();
  }
}

/**
 * Atomic join success commit (final TOCTOU close).
 * Cancel latch is never cleared before media is proven live for this call.
 * If cancel/release races the final edge (including during isActive probes),
 * re-latch, scoped-release, and throw — join must not resolve into callActive
 * with dead media, and must not clear a cancel that already released B.
 *
 * After the latch is cleared, yield one microtask so any already-queued cancel
 * handlers (ActionCable/event microtasks) can re-latch before join resolves.
 * @param {number|string} callRecordId
 */
async function commitJoinSuccess(callRecordId) {
  // Pre-clear proofs: cancel may have latched during arm/recording checks.
  assertJoinNotCancelled(callRecordId);
  assertMediaActiveForCall(callRecordId);
  // Cancel may latch as a side-effect of the media probe itself.
  assertJoinNotCancelled(callRecordId);

  // Only free the latch after both cancel and live-media proofs hold.
  clearJoinCancelled(callRecordId);

  // Drain one microtask boundary so cancel queued during the final probes
  // (or while clearing) can re-latch before we declare join success.
  await Promise.resolve();

  // Post-clear safety: media must still be owned. If release raced the probe
  // (or cancel re-latched via another path), do not return success.
  if (isJoinCancelled(callRecordId)) {
    releaseStackZapMediaForCall(callRecordId);
    throw joinCancelledError();
  }
  try {
    assertMediaActiveForCall(callRecordId);
  } catch {
    markJoinCancelled(callRecordId);
    releaseStackZapMediaForCall(callRecordId);
    throw joinCancelledError();
  }
  // Final re-check: cancel during post-clear media probe must not succeed.
  if (isJoinCancelled(callRecordId)) {
    releaseStackZapMediaForCall(callRecordId);
    throw joinCancelledError();
  }
}

function cableUrl() {
  const host =
    (typeof window !== 'undefined' && window.chatwootConfig?.websocketURL) ||
    '';
  return host ? `${host}/cable` : undefined;
}

export function stackZapWidgetKey(callRecordId) {
  return `stackzap:${callRecordId}`;
}

export function isStackZapCall(call) {
  return call?.provider === VOICE_CALL_PROVIDERS.STACKZAP;
}

function normalizeContact(contact) {
  if (!contact || typeof contact !== 'object') return null;
  return {
    id: contact.id,
    name: contact.name,
    phone: contact.phone || contact.phone_number,
    avatar: contact.avatar_url || contact.avatar || contact.thumbnail,
    additionalAttributes: contact.additional_attributes || {},
  };
}

/**
 * Parse account_id from cable payload. Null when missing/invalid (fail-closed).
 * @param {object} data
 * @returns {number|null}
 */
function payloadAccountId(data) {
  const raw = data?.account_id ?? data?.accountId;
  if (raw == null || raw === '') return null;
  const id = Number(raw);
  return Number.isFinite(id) && id > 0 ? id : null;
}

/**
 * Fail-closed: event must carry a positive account_id matching the active listener.
 * Do not touch store/media for foreign or unscoped payloads.
 * @param {object} data
 * @returns {boolean}
 */
function isValidAccountScopedPayload(data) {
  if (!listener) return false;
  const accountId = payloadAccountId(data);
  if (accountId == null) return false;
  return Number(accountId) === Number(listener.accountId);
}

function callFromIncomingPayload(payload) {
  const callRecordId = payload.call_record_id;
  if (!callRecordId) return null;
  const accountId = payloadAccountId(payload);
  if (accountId == null) return null;
  return {
    callSid: stackZapWidgetKey(callRecordId),
    callRecordId,
    accountId,
    provider: VOICE_CALL_PROVIDERS.STACKZAP,
    providerCallId: payload.provider_call_id || null,
    wahaCallId: payload.waha_call_id || null,
    // Internal PK for APIs that still require it.
    conversationId: payload.conversation_id,
    // Public display_id for URL/label (never use internal PK in routes).
    conversationDisplayId:
      payload.conversation_display_id ?? payload.conversationDisplayId ?? null,
    inboxId: payload.inbox_id,
    voipDeviceId: payload.voip_device_id,
    messageId: payload.message_id,
    callDirection: VOICE_CALL_DIRECTION.INBOUND,
    status: 'incoming',
    caller: normalizeContact(payload.contact),
  };
}

function mergeCallUpdate(existing, payload) {
  const next = { ...existing };
  if (payload.provider_call_id) next.providerCallId = payload.provider_call_id;
  if (payload.waha_call_id) next.wahaCallId = payload.waha_call_id;
  if (payload.final_status) next.status = payload.final_status;
  if (payload.conversation_id) next.conversationId = payload.conversation_id;
  if (
    payload.conversation_display_id != null ||
    payload.conversationDisplayId != null
  ) {
    next.conversationDisplayId =
      payload.conversation_display_id ?? payload.conversationDisplayId;
  }
  const accountId = payloadAccountId(payload);
  if (accountId != null) next.accountId = accountId;
  if (payload.inbox_id) next.inboxId = payload.inbox_id;
  return next;
}

function findByRecordId(callsStore, callRecordId) {
  const key = stackZapWidgetKey(callRecordId);
  return callsStore.calls.find(
    c =>
      c.callSid === key ||
      c.callRecordId === callRecordId ||
      Number(c.callRecordId) === Number(callRecordId)
  );
}

function shouldTriggerDisposition(config, finalStatus) {
  const triggerOn = config?.trigger_on || {};
  const map = {
    ended: 'answered',
    missed: 'missed',
    rejected: 'rejected',
    failed: 'failed',
  };
  const bucket = map[String(finalStatus)];
  return !!(bucket && triggerOn[bucket] === true);
}

async function maybeOpenDisposition(store, callRecordId) {
  if (!store || callRecordId == null || callRecordId === '') return;

  const key = String(callRecordId);
  if (dispositionDone.has(key) || dispositionInFlight.has(key)) return;

  dispositionInFlight.add(key);
  try {
    const { data: callRecord } = await CallRecordsAPI.show(callRecordId);
    if (!callRecord) return;

    // Definitive skip: already filled.
    if (callRecord.disposition_filled_at) {
      dispositionDone.add(key);
      return;
    }

    const config = store.getters['voipDisposition/getConfig'];
    // Temporary: config not loaded yet — allow retry later.
    if (!config) return;

    const ownerId =
      callRecord.agent_user_id ?? callRecord.agent_user?.id ?? null;
    const currentUserId = store.getters.getCurrentUserID;
    // Definitive skip: another agent owns the call (numeric/string safe).
    if (
      ownerId != null &&
      currentUserId != null &&
      Number(ownerId) !== Number(currentUserId)
    ) {
      dispositionDone.add(key);
      return;
    }

    // Definitive skip: trigger disabled for this status.
    if (!shouldTriggerDisposition(config, callRecord.final_status)) {
      dispositionDone.add(key);
      return;
    }

    // Await so a rejected async dispatch stays retryable (not consumed).
    await store.dispatch('voipDisposition/showModal', {
      callRecord,
      pendingCallEnd: null,
    });
    dispositionDone.add(key);
  } catch {
    // API/dispatch failure — do not mark done so a later terminal event can retry.
  } finally {
    dispositionInFlight.delete(key);
  }
}

function handleIncoming(callsStore, payload) {
  const call = callFromIncomingPayload(payload);
  if (!call) return;
  callsStore.addCall(call);
}

function handleUpdated(callsStore, store, payload) {
  const callRecordId = payload.call_record_id;
  if (!callRecordId) return;

  const existing = findByRecordId(callsStore, callRecordId);
  const status = payload.final_status?.toString?.() || payload.final_status;
  const accountId = payloadAccountId(payload);

  if (TERMINAL.has(status)) {
    if (existing) {
      if (existing.isActive) {
        // Connected call ended remotely: finalize recording once, then clear.
        // Fire-and-forget so channel handler stays non-blocking; media always cleans.
        finalizeAndUploadRecording(callRecordId).finally(() => {
          callsStore.clearActiveCall();
        });
      } else {
        // Never-connected / ringing terminal (failed/missed/rejected/ended pre-join).
        // Latch so in-flight join of B cannot resume; release only B's prewarm/session.
        // Never stopStackZapMedia — stacked active call A must stay intact.
        markJoinCancelled(callRecordId);
        releaseStackZapMediaForCall(callRecordId);
        callsStore.dismissCall(existing.callSid);
      }
    } else {
      // Terminal without a local widget: outbound create-in-flight (response not
      // yet inserted) or already-dismissed entry. Latch + scoped release so a
      // later create/add/auto-join of B cannot start media or stop active A.
      // Mirrors handleCancelled's no-existing path; never global stop.
      markJoinCancelled(callRecordId);
      releaseStackZapMediaForCall(callRecordId);
    }
    maybeOpenDisposition(store, callRecordId);
    return;
  }

  if (existing) {
    const merged = mergeCallUpdate(existing, payload);
    callsStore.addCall(merged);
    // Outbound (and any live session): arm recorder only on durable connected state.
    if (status === 'in_progress') {
      armStackZapRecording();
    }
    return;
  }

  // Late update without a prior incoming — seed a minimal non-active entry.
  // Fail-closed: require account_id so foreign lifecycle cannot invent a widget.
  if (accountId == null) return;
  callsStore.addCall({
    callSid: stackZapWidgetKey(callRecordId),
    callRecordId,
    accountId,
    provider: VOICE_CALL_PROVIDERS.STACKZAP,
    providerCallId: payload.provider_call_id || null,
    wahaCallId: payload.waha_call_id || null,
    conversationId: payload.conversation_id,
    conversationDisplayId:
      payload.conversation_display_id ?? payload.conversationDisplayId ?? null,
    inboxId: payload.inbox_id,
    callDirection: VOICE_CALL_DIRECTION.INBOUND,
    status: status || 'ringing',
  });
}

function handleCancelled(callsStore, payload, currentUserId) {
  const callRecordId = payload.call_record_id;
  const acceptedBy = payload.accepted_by;
  if (
    acceptedBy != null &&
    currentUserId != null &&
    Number(acceptedBy) === Number(currentUserId)
  ) {
    // Current user just accepted — do not cancel their local entry.
    return;
  }

  let dismissedSid = null;

  if (callRecordId) {
    // Latch before release so any in-flight join/create cannot resume past
    // accept/start and re-open orphan mic/media for this id.
    markJoinCancelled(callRecordId);
    const existing = findByRecordId(callsStore, callRecordId);
    if (existing) {
      // Non-active create→join: release only this call's prewarm/session.
      // Active: same call-scoped stop (never global stop for foreign media).
      releaseStackZapMediaForCall(callRecordId);
      if (existing.isActive) {
        callsStore.clearActiveCall();
      } else {
        callsStore.dismissCall(existing.callSid);
      }
      dismissedSid = existing.callSid;
    } else {
      // Widget may already be gone; still drop tagged prewarm for this id.
      // Create-in-flight: latch alone blocks late add/join when response arrives.
      releaseStackZapMediaForCall(callRecordId);
    }
  }

  // Fallback by WAHA id (also when call_record_id was present but unmatched).
  // Prefer match.callRecordId for scoped media release when available.
  if (payload.waha_call_id) {
    const match = callsStore.calls.find(
      c => c.wahaCallId === payload.waha_call_id
    );
    if (match && match.callSid !== dismissedSid) {
      const matchRecordId = match.callRecordId || callRecordId;
      if (matchRecordId) {
        markJoinCancelled(matchRecordId);
        releaseStackZapMediaForCall(matchRecordId);
      }
      if (match.isActive) {
        callsStore.clearActiveCall();
      } else {
        callsStore.dismissCall(match.callSid);
      }
    }
  }
}

function handleTestRing(payload) {
  // Simple aviso only — never open the accept/reject call widget.
  const label = payload.device_label || payload.deviceLabel || '';
  emitter.emit('voipRichToast', {
    type: 'call.test',
    title: label ? `Teste de toque · ${label}` : 'Teste de toque',
    body: 'Este é um toque de teste. Não é uma ligação real entrando.',
    tag: `call-test-${payload.voip_device_id || payload.voipDeviceId || 'x'}`,
    created_at: new Date().toISOString(),
  });
}

function onChannelMessage(callsStore, store, data) {
  if (!data || typeof data !== 'object') return;
  // Fail-closed account isolation: reject missing/mismatched account_id before
  // any store or media side-effect. Backend stream is account-scoped; this is
  // defense-in-depth when a stale or misrouted payload still arrives.
  if (!isValidAccountScopedPayload(data)) return;

  const type = data.type;
  if (type === 'call.test') {
    handleTestRing(data);
    return;
  }
  if (type === 'voip.incoming_call') {
    handleIncoming(callsStore, data);
    return;
  }
  if (type === 'call.updated') {
    handleUpdated(callsStore, store, data);
    return;
  }
  if (type === 'call.cancelled') {
    handleCancelled(callsStore, data, store.getters.getCurrentUserID);
    return;
  }
  if (type === 'copilot.card') {
    useCallCopilotStore().addCard(data);
    return;
  }
  if (type === 'copilot.status') {
    useCallCopilotStore().setStatus(data);
  }
}

function clearStackZapCallsOnly() {
  try {
    useCallsStore().clearStackZapCalls();
  } catch {
    /* pinia may be unmounted in tests */
  }
}

function teardownListener() {
  if (!listener) return;
  try {
    listener.subscription?.unsubscribe();
  } catch {
    /* ignore */
  }
  try {
    listener.consumer?.disconnect();
  } catch {
    /* ignore */
  }
  stopStackZapMedia();
  listener = null;
}

/**
 * Subscribe (or replace) the account VoipCallChannel listener.
 * Idempotent across account switches and hot reloads.
 * On account replacement, drops only StackZap store entries (not Twilio/Meta).
 */
export function initStackZapCallListener({
  accountId,
  userId,
  pubsubToken,
  store,
}) {
  if (!accountId || !userId || !pubsubToken || !store) return;

  if (
    listener &&
    String(listener.accountId) === String(accountId) &&
    String(listener.userId) === String(userId) &&
    listener.pubsubToken === pubsubToken
  ) {
    return;
  }

  teardownListener();
  clearStackZapCallsOnly();

  const callsStore = useCallsStore();
  const consumer = createConsumer(cableUrl());
  const subscription = consumer.subscriptions.create(
    {
      channel: 'VoipCallChannel',
      pubsub_token: pubsubToken,
      user_id: userId,
      account_id: accountId,
    },
    {
      connected() {
        // Diagnostico: backend OK, widget nao abriu.
        // eslint-disable-next-line no-console -- fecha o lado do browser numa ligacao de teste
        console.info('[VoipCallChannel] connected', { accountId, userId });
      },
      rejected() {
        // eslint-disable-next-line no-console -- assinatura recusada era silenciosa
        console.warn('[VoipCallChannel] rejected', { accountId, userId });
      },
      received(data) {
        // eslint-disable-next-line no-console -- so tipo e conta, sem contato
        console.info('[VoipCallChannel] received', {
          type: data?.type,
          account_id: data?.account_id,
        });
        onChannelMessage(callsStore, store, data);
      },
    }
  );

  listener = {
    accountId,
    userId,
    pubsubToken,
    consumer,
    subscription,
    store,
    dispositionDone,
  };
}

/**
 * Full StackZap surface teardown: cable + media + StackZap-only store widgets.
 * Safe to call on feature-off, account switch, and unmount.
 */
export function stopStackZapCallListener() {
  teardownListener();
  clearStackZapCallsOnly();
  stopStackZapMedia();
}

/**
 * App account init: either start the current account listener or stop prior.
 * Fail-closed when the VoIP feature is off OR identity is incomplete
 * (missing account/user/pubsub/store) so a prior account never stays subscribed.
 */
export function syncStackZapCallListenerForAccount({
  accountId,
  userId,
  pubsubToken,
  store,
  featureEnabled,
}) {
  const identityComplete = !!(
    featureEnabled &&
    accountId &&
    userId &&
    pubsubToken &&
    store
  );

  if (!identityComplete) {
    // Diagnostico: backend OK, widget nao abriu. Sem token/PII.
    // eslint-disable-next-line no-console -- precisa sobreviver ao boot fail-closed
    console.warn('[VoipCallChannel] listener not started', {
      featureEnabled: !!featureEnabled,
      hasAccountId: !!accountId,
      hasUserId: !!userId,
      hasPubsubToken: !!pubsubToken,
      hasStore: !!store,
    });
    stopStackZapCallListener();
    return;
  }

  initStackZapCallListener({ accountId, userId, pubsubToken, store });
}

/** Test helper: expose active listener refs for unsubscribe/disconnect asserts. */
export function getStackZapListenerForTests() {
  return listener;
}

export async function startOutboundStackZap({ conversationId }) {
  // Click path: unlock AudioContext before create API await loses activation.
  // Unowned until create returns — cancel of an unrelated id must not steal it.
  // If call A already owns a live session, prewarm only resumes A (no new holder).
  prewarmStackZapAudio();

  const callsStore = useCallsStore();
  try {
    const { data } = await voipCallsAPI.create(conversationId, {});
    const callRecordId = data?.call_record_id;
    if (!callRecordId) {
      throw new Error('call_failed');
    }

    // Cancel arrived while create was in flight (before local id was known):
    // never add a dead widget; release only this attempt's prewarm (tag→release).
    // Never fullStop — that would destroy an already-active foreign call A.
    if (isJoinCancelled(callRecordId)) {
      prewarmStackZapAudio(callRecordId);
      releaseStackZapMediaForCall(callRecordId);
      disposeUnownedStackZapPrewarm();
      throw joinCancelledError();
    }

    // Tag prewarm ownership for create→join cancel (call.cancelled) cleanup.
    // No-op retag when a foreign call already owns the holder (A protected).
    prewarmStackZapAudio(callRecordId);

    // Cancel between tag and widget insert — release owned prewarm only.
    if (isJoinCancelled(callRecordId)) {
      releaseStackZapMediaForCall(callRecordId);
      disposeUnownedStackZapPrewarm();
      throw joinCancelledError();
    }

    const callSid = stackZapWidgetKey(callRecordId);
    callsStore.addCall({
      callSid,
      callRecordId,
      provider: VOICE_CALL_PROVIDERS.STACKZAP,
      providerCallId: data.provider_call_id || null,
      conversationId: data.conversation_id || conversationId,
      inboxId: data.inbox_id,
      callDirection: VOICE_CALL_DIRECTION.OUTBOUND,
      status: 'ringing',
    });

    // Keep prewarmed context for join; claim happens on media start.
    return { callSid, callRecordId, data };
  } catch (err) {
    if (isJoinCancelledError(err)) {
      // Scoped cleanup already done above when id was known.
      throw err;
    }
    // Create reject / missing id: drop unowned gesture prewarm only.
    // Never stopStackZapMedia — must not tear down active call A.
    disposeUnownedStackZapPrewarm();
    throw err;
  }
}

export async function joinStackZapCall({
  call,
  pubsubToken,
  userId,
  accountId,
}) {
  if (!isStackZapCall(call) || !call.callRecordId) {
    throw new Error('invalid_call');
  }

  const callRecordId = call.callRecordId;

  // Already cancelled (e.g. remote cancel before click finishes) — no accept/media.
  assertJoinNotCancelled(callRecordId);

  // Click path (inbound accept / outbound auto-join after start prewarm):
  // create/resume AudioContext before accept API or getUserMedia awaits.
  // Tag with callRecordId so remote cancel during join is call-scoped.
  // Will not retag a foreign-owned prewarm (A protected).
  prewarmStackZapAudio(callRecordId);

  // Cancel may race the prewarm itself (dismiss on another tab/event).
  assertJoinNotCancelled(callRecordId);

  const isOutbound = call.callDirection === VOICE_CALL_DIRECTION.OUTBOUND;

  try {
    if (!isOutbound) {
      await withCallBindingPendingRetry(() =>
        voipCallsAPI.accept(callRecordId)
      );
      // Cancel during accept: never proceed to media; do not hang up others.
      assertJoinNotCancelled(callRecordId);
    }

    await startStackZapMedia({
      pubsubToken,
      userId,
      accountId,
      callRecordId,
    });

    // Post-start fail-closed gate (closes silent session_stale / TOCTOU window):
    // 1) cancel latch may have flipped during start
    // 2) media must be active AND owned by this callRecordId
    // 3) final cancel check before any success side-effect
    assertJoinNotCancelled(callRecordId);
    assertMediaActiveForCall(callRecordId);
    assertJoinNotCancelled(callRecordId);

    // Inbound: accept already proved connection — arm once.
    // Outbound: media may open while ringing; wait for call.updated in_progress.
    // Close race: in_progress may arrive before media start resolves — re-check
    // Pinia state after start and arm exactly once when already connected.
    if (!isOutbound) {
      armStackZapRecording();
    } else {
      const callsStore = useCallsStore();
      const current = findByRecordId(callsStore, callRecordId);
      if (current?.status === 'in_progress') {
        armStackZapRecording();
      }
    }

    // Atomic success commit: cancel latch must never clear before media is proven
    // live. Final edge cancel/release (probe side-effect, microtask re-latch) aborts
    // join so parent cannot setCallActive on dead media for B.
    await commitJoinSuccess(callRecordId);
  } catch (err) {
    // Cancellation-specific path: scoped release only. Never provider hangup —
    // hangup here can tear down another agent who already accepted this call.
    // Never fullStop global — releaseForCall only touches this call's media.
    if (isJoinCancelledError(err) || isJoinCancelled(callRecordId)) {
      releaseStackZapMediaForCall(callRecordId);
      throw isJoinCancelledError(err) ? err : joinCancelledError();
    }
    try {
      await voipCallsAPI.hangup(callRecordId);
    } catch {
      /* best-effort hangup on genuine accept/media failure */
    }
    // Accept/media failure: release only this call's session/prewarm.
    // Do not stopStackZapMedia — that would destroy foreign active call A.
    releaseStackZapMediaForCall(callRecordId);
    disposeUnownedStackZapPrewarm();
    throw err;
  }
}

export async function endStackZapCall(call) {
  const callRecordId = call?.callRecordId;
  try {
    if (callRecordId) {
      // Hangup is already bound — never binding-pending retry.
      await voipCallsAPI.hangup(callRecordId);
    }
  } catch {
    // Provider hangup may fail; local media/mic must still close.
  } finally {
    // Finalize recorder + upload (if any), always clean mic/media.
    // Only connected/active sessions produce a non-empty blob.
    await finalizeAndUploadRecording(callRecordId);
  }
}

export async function rejectStackZapCall(call) {
  if (!call?.callRecordId) return;
  const callRecordId = call.callRecordId;
  // Rejected / never-connected: no MediaRecorder, no upload.
  // Latch + scoped release only — never stopStackZapMedia (protects active A).
  markJoinCancelled(callRecordId);
  releaseStackZapMediaForCall(callRecordId);
  const isOutbound = call.callDirection === VOICE_CALL_DIRECTION.OUTBOUND;
  if (isOutbound) {
    await voipCallsAPI.hangup(callRecordId);
  } else {
    await withCallBindingPendingRetry(() =>
      voipCallsAPI.rejectStackZap(callRecordId)
    );
  }
}

export function setStackZapCallMuted(muted) {
  setStackZapMediaMuted(!!muted);
}

export function resetStackZapCallSessionForTests() {
  teardownListener();
  clearStackZapCallsOnly();
  dispositionDone.clear();
  dispositionInFlight.clear();
  cancelledJoinIds.clear();
}

// Exported for focused unit tests without ActionCable.
export const testables = {
  onChannelMessage,
  callFromIncomingPayload,
  payloadAccountId,
  isValidAccountScopedPayload,
  stackZapWidgetKey,
  withCallBindingPendingRetry,
  isCallBindingPendingError,
  finalizeAndUploadRecording,
  handleCancelled,
  handleUpdated,
  handleIncoming,
  maybeOpenDisposition,
  dispositionDone,
  dispositionInFlight,
  markJoinCancelled,
  isJoinCancelled,
  clearJoinCancelled,
  isJoinCancelledError,
  commitJoinSuccess,
  JOIN_CANCELLED,
  cancelledJoinIds,
  TERMINAL,
};
